import React, { useMemo, useRef, useState } from "react";
import { 
    Container, Typography, Paper, Grid, Button, FormControl, 
    InputLabel, Select, MenuItem, Dialog, DialogContent, 
    Box, CircularProgress, Stack 
} from "@mui/material";
import { useAuth } from "../../../hooks/useAuth";
import { 
    getInventoryLogsAPI, getMenuInventory, getStoreInfo, getUserTimeLogAPI 
} from "../../../hooks/apiHandler";
import { ClockInReportPage } from "./ClockInReportPage";
import { TipReportPage } from "./tipReport";
import { EmployeeReportPage } from "./EmployeeReportPage";
import { SalesReportPage } from "./SalesReportPage";
import { OrderReportPage } from "./OrderReportPage";
import { ItemSalesReportPage } from "./ItemSalesReportPage";

// Mock/Placeholder Components
const DetailReportPage = () => <Typography variant="h4">Detail Report Content</Typography>;
const ExpenseReportPage = () => <Typography variant="h4">Expense Report Content</Typography>;
const CashDropReportPage = () => <Typography variant="h4">Cash Drop Report Content</Typography>;


// On Mobile (xs): Title on top, buttons below (full width or wrapped).
// On Desktop (md): Title on left, buttons on right.
const ReportRow = ({ title, children, controls }) => (
    <Box sx={{ 
        py: 2, 
        borderBottom: '1px solid rgba(0,0,0,0.05)', 
        '&:last-child': { borderBottom: 'none' } 
    }}>
        <Grid container spacing={2} alignItems="center">
            {/* Title Section */}
            <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" fontWeight={500} color="#222">
                    {title}
                </Typography>
            </Grid>

           
            <Grid item xs={12} md={7}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: { xs: 'flex-start', md: 'flex-end' }, 
                    alignItems: 'center',
                    flexWrap: 'wrap', 
                    gap: 1 
                }}>
                  
                    {controls && (
                        <Box sx={{ width: { xs: '100%', sm: 'auto' }, mr: { sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                            {controls}
                        </Box>
                    )}
                    {children}
                </Box>
            </Grid>
        </Grid>
    </Box>
);

export const CustomReports = ({ startDate, endDate, storeID, rawDataParent }) => {
    const { Logout } = useAuth();
    
    // UI State
    const [isLoading, setIsLoading] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    
    // Legacy PDF State
    const [pdfData, setPdfData] = useState({ report: '', rawData: [] });
    const printRef = useRef(null);

    // Store Info
    const [storeInfo, setStoreInfo] = useState(null);
    useMemo(() => {
        const fetchInfo = async () => {
            const response = await getStoreInfo({ storeID }, Logout);
            if (response) setStoreInfo(response.data);
        };
        fetchInfo();
    }, [storeID, Logout]);

    // Derived Employee List
    const employeeList = useMemo(() => {
        const sourceData = pdfData?.rawData?.length > 0 ? pdfData.rawData : rawDataParent || [];
        const employees = new Set();
        
        // Handle potentially different data structures
        const dataList = Array.isArray(sourceData) ? sourceData : (sourceData.orders || sourceData.data || []);

        dataList.forEach(order => {
            const name = order.orders?.[0]?.cName;
            if (name) employees.add(name);
        });
        return [...employees];
    }, [rawDataParent, pdfData]);

    // --- GENERIC HANDLER to reduce code duplication ---
    const handleOpenReport = (storageKey, route, customPayload = null) => {
        try {
            const payload = customPayload || {
                storeID,
                startDate,
                endDate,
                rawData: rawDataParent || []
            };
            sessionStorage.setItem(storageKey, JSON.stringify(payload));
            window.open(route, "_blank");
        } catch (err) {
            console.error(`Failed to open ${route}:`, err);
        }
    };

    // --- Specific Handlers ---

    // 1. Employee Report (Filtered)
    const handleEmployeeReportOpen = (action) => {
        if (!selectedEmployee) return alert("Please select an employee first.");

        let sourceData = rawDataParent || [];
        if (sourceData.orders) sourceData = sourceData.orders;
        else if (sourceData.data) sourceData = sourceData.data;

        // Filter Data
        const filteredData = Array.isArray(sourceData) ? sourceData.filter(order => {
             return order.orders?.[0]?.cName === selectedEmployee;
        }) : [];

        // Logic split based on your original code's "view" vs "open"
        if(action === 'open') {
             handleOpenReport("employeeReportData", "/epr", {
                storeID, startDate, endDate, rawData: filteredData
            });
        }
        // Add other actions (view/download) here if you re-enable them later
    };

    // 2. Clock In Report (API Call)
    const handleClockInReport = async () => {
        setIsLoading(true);
        try {
            const rawData = await getUserTimeLogAPI({ storeID, startDate, endDate }, Logout);
            handleOpenReport("clockInReportData", "/clock-in", {
                storeID, startDate, endDate, rawData: rawData?.data
            });
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // 3. Inventory Logs (Complex API Call)
    const handleInventoryLogReport = async () => {
        setIsLoading(true);
        try {
            const logTypes = ['DELIVERY', 'COUNT_ADJUSTMENT', 'SPOILAGE', 'RECIPE_CONSUMPTION', 'INTERNAL_USE'];
            const [itemResponse, ...logResponses] = await Promise.all([
                getMenuInventory({ storeID, startDate, endDate }, Logout),
                ...logTypes.map(type => getInventoryLogsAPI({ storeID, startDate, endDate, currentLogType: type }, Logout))
            ]);

            const logs = {};
            logResponses.forEach((res, index) => logs[logTypes[index]] = res.data || []);

            handleOpenReport("inventoryLogReportData", "/inventory-report-logs", {
                items: itemResponse.data || [],
                logs,
                generatedAt: new Date().toISOString()
            });
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // 4. Stock Report (API Call)
    const handleStockReport = async () => {
        setIsLoading(true);
        try {
            const response = await getMenuInventory({ storeID, startDate, endDate }, Logout);
            handleOpenReport("inventoryReportData", "/inventory-report", {
                rawData: response.data || []
            });
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const reportComponents = {
        'clock-in-report': <ClockInReportPage rawData={null} />,
        'tip-report': <TipReportPage rawData={null} />,
        'ep-report': <EmployeeReportPage rawData={null} />,
        'dep-report': <DetailReportPage />,
        'sales-report': <SalesReportPage rawData={null} />,
        'order-report': <OrderReportPage rawData={null} />,
        'payment-report': <OrderReportPage rawData={null} />,
        'item-report': <ItemSalesReportPage rawData={null} />,
        'exp-report': <ExpenseReportPage />,
        'cd-report': <CashDropReportPage />,
    };

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            
            {/* --- SECTION 1: EMPLOYEE REPORTS --- */}
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, my: 3, backgroundColor: "#f3faff" }}>
                <Typography variant="h6" color="primary" gutterBottom sx={{ mb: 2 }}>
                    Employee Reports
                </Typography>

                <ReportRow title="Clock-in Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={handleClockInReport}
                        disabled={isLoading}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                <ReportRow title="Tip Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleOpenReport("tipReportData", "/tip")}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                {/* Filtered Employee Report */}
                <ReportRow 
                    title="Detail Report (By User)"
                    controls={
                        <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id="emp-select-label">Select Employee</InputLabel>
                            <Select
                                labelId="emp-select-label"
                                value={selectedEmployee}
                                label="Select Employee"
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {employeeList.map((emp, i) => (
                                    <MenuItem key={i} value={emp}>{emp}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    }
                >
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleEmployeeReportOpen('open')}
                    >
                        View/Download
                    </Button>
                </ReportRow>
                
                {/* General Employee Report */}
                <ReportRow title="Employee Report (General)">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleOpenReport("employeeReportData", "/epr")}
                    >
                        View/Download
                    </Button>
                </ReportRow>
            </Paper>

            {/* --- SECTION 2: STORE REPORTS --- */}
            <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, my: 3, backgroundColor: "#fff3e0" }}>
                <Typography variant="h6" color="warning.main" gutterBottom sx={{ mb: 2 }}>
                    Store Reports
                </Typography>

                <ReportRow title="Sale Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleOpenReport("salesReportData", "/sales-report")}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                <ReportRow title="Order Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleOpenReport("orderReportData", "/orders-report")}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                <ReportRow title="Payment Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleOpenReport("orderReportData", "/orders-report")}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                <ReportRow title="Item Sale Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={() => handleOpenReport("itemSaleReportData", "/item-report")}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                <ReportRow title="Inventory Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={handleStockReport}
                        disabled={isLoading}
                    >
                        View/Download
                    </Button>
                </ReportRow>

                <ReportRow title="Inventory Logs Report">
                    <Button 
                        variant="outlined" 
                        color="primary"
                        onClick={handleInventoryLogReport}
                        disabled={isLoading}
                    >
                        View/Download
                    </Button>
                </ReportRow>
            </Paper>

            {/* Loading Modal */}
            <Dialog open={isLoading} fullWidth maxWidth="sm">
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Generating report...</Typography>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Hidden PDF Generator (Legacy Support) */}
            <div style={{ position: 'absolute', top: '-9999px', left: '-9999px' }}>
                {pdfData.report && React.cloneElement(reportComponents[pdfData.report], { ref: printRef, rawData: pdfData.rawData })}
            </div>
        </Container>
    );
};